/**
 * DIBELS8 gating rules service.
 * @module gating_rules_service
 *
 */
const camelCase = require('lodash.camelcase');
const GATING_RULES_GROUPED_BY_WINDOW = require('dibels8-content').gatingRules;
const { getScoresInfoByWindow, has } = require('./lib');

const GATED_MEASURES = 'gated_measures';
const GATING_MEASURE = 'gating_measure';
const IMPUTED_SCORE = 'imputed_score';
const IMPUTED_SCORE_INTERPRETATION = 'imputed_score_interpretation';
const RULE_CRITERIA = 'rule_criteria';
const RULE_TYPE = 'rule_type';

/**
 * Return gated measure object.
 *
 * @param {String} gatingType - gating rule type
 * @param {Number} imputedScore - gated measure score
 * @param {String} interpretation - gated measure score interpretation
 * @param {String} serverCreateDate - gating measure sync date
 * @param {String} serverModDate - gating measure last modification date
 * @param {String} clientCreateDate - gating measure create date on the device
 * @param {String} clientModDate - gating measure last modification date on the device
 */
function getGatedMeasureObj(gatingType, imputedScore, interpretation,
    serverCreateDate, serverModDate, clientCreateDate, clientModDate) {
    return {
        gatingType,
        imputedScore,
        interpretation,
        ...(serverCreateDate) && { serverCreateDate },
        ...(serverModDate) && { serverModDate },
        ...(clientCreateDate) && { clientCreateDate },
        ...(clientModDate) && { clientModDate },
    };
}

/**
 * Get gating rules data for all windows.
 */
function getAllGatingRules() {
    return GATING_RULES_GROUPED_BY_WINDOW;
}

/**
 * Get gating rule data for a window.
 * @param {String} grade - student's grade
 * @param {String} period - time of the year
 * @returns {Object} Object with gating rule data for a window.
 */
function getGatingRuleForWindow(grade, period) {
    return GATING_RULES_GROUPED_BY_WINDOW[`${grade}_${period}`];
}

/**
 * Return a flag that indicates gating procedure is met.
 *
 * @param {String} grade - student's grade
 * @param {String} period - time of the year
 * @param {Object} scoresInfo - student's measure scores
 * @returns {Boolean} boolean value to indicate that gating procedure is met for the students' results.
 */
function isGatingProcedureMet(grade, period, scoresInfo) {
    const windowGatingRule = getGatingRuleForWindow(grade, period);
    if (windowGatingRule !== undefined) {
        const resultForGatingMeasure = scoresInfo[windowGatingRule[GATING_MEASURE]];
        if (resultForGatingMeasure !== undefined) {
            const ruleAttribute = windowGatingRule[RULE_CRITERIA].name;
            const ruleValue = windowGatingRule[RULE_CRITERIA].value;
            return resultForGatingMeasure[camelCase(ruleAttribute)] === ruleValue;
        }
    }
    return false;
}

/**
 * Return gated measure scores for the case when gating procedure is met.
 *
 * @param {String} grade - student's grade
 * @param {String} period - time of the year
 * @param {Object} measureScores - student's measure scores
 */
function getGatedMeasureScores(grade, period, measureScores) {
    if (isGatingProcedureMet(grade, period, measureScores)) {
        const windowGatingRule = getGatingRuleForWindow(grade, period);
        const resultForGatingMeasure = measureScores[windowGatingRule[GATING_MEASURE]];
        return Object.entries(windowGatingRule[GATED_MEASURES])
            .reduce((gatedMeasures, [gatedMeasure, gatedMeasureData]) => {
                if (!has.call(measureScores, gatedMeasure)) {
                    return {
                        ...gatedMeasures,
                        [gatedMeasure]: getGatedMeasureObj(
                            windowGatingRule[RULE_TYPE],
                            gatedMeasureData[IMPUTED_SCORE],
                            gatedMeasureData[IMPUTED_SCORE_INTERPRETATION],
                            resultForGatingMeasure.serverCreateDate,
                            resultForGatingMeasure.serverModDate,
                            resultForGatingMeasure.clientCreateDate,
                            resultForGatingMeasure.clientModDate,
                        ),
                    };
                }
                return gatedMeasures;
            }, {});
    }
    return {};
}

/**
 * Return gated measure scores for student results.
 * @param {Array} studentResults - list of student results
 */
function getGatedMeasureScoresForStudent(studentResults) {
    const scoresInfoByWindow = getScoresInfoByWindow(studentResults);
    const gatedMeasuresGroupedByWindow = {};
    Object.keys(scoresInfoByWindow).forEach((grade) => {
        gatedMeasuresGroupedByWindow[grade] = {};
        Object.keys(scoresInfoByWindow[grade]).forEach((period) => {
            gatedMeasuresGroupedByWindow[grade][period] = getGatedMeasureScores(
                grade, period, scoresInfoByWindow[grade][period].regular,
            );
        });
    });
    return gatedMeasuresGroupedByWindow;
}

module.exports = {
    getAllGatingRules,
    getGatedMeasureScores,
    getGatedMeasureScoresForStudent,
    getGatingRuleForWindow,
    isGatingProcedureMet,
};
